import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import createOrderApi, { Metadata, OrderParams, PlaceOrderSummary } from 'api/order';
import { omit } from 'lodash-es';
import { showToast } from 'modules/app/toasterSlice';
import { appContextSelector } from 'modules/app/appSlice';
import { currentUserSelector, metadatasSelector } from 'modules/user/userSlice';
import { syncAttribution } from 'modules/user/attributionSlice';
import { singlePorfolioSanityIdSelector, virtualPortfoliosSelector } from './portfolioSelectSlice';
import { AppThunk, RootState } from 'store';
import { getDisplayName } from 'utils/metadata';
import { fixFloat } from 'utils/numberFormatter';
import axios, { CancelTokenSource } from 'axios';
import createAirFreightApi from 'api/airFreight';
import { loadBills } from 'modules/billing/billingSlice';
import { getOrderStatus } from 'pages/shared/CorporateOffset/helpers/getOrderStatus';
import { setTag, tags } from 'utils/tags';
import { transactionProductsName } from 'utils/order';
import checkJob from 'utils/checkJob';
import { validateItemPortfolio } from 'pages/shared/CorporateOffset/helpers/validateItemPortfolio';

const CancelToken = axios.CancelToken;
let source: CancelTokenSource = CancelToken.source();

//region types
interface OffsetInfo {
  orderName?: string;
  country?: Option;
  department?: Option;
  description?: string;
  emissionScope?: Option;
  timeFrameFrom?: Date;
  timeFrameTo?: Date;
  [key: string]: string | Option | Date | undefined | boolean | number;
}
interface OrderPayload {
  number: string;
  date: string;
  name?: string;
  id: string;
  claimCode?: string;
  inviteId?: string;
  billId?: string;
  orderStatus?: string;
}

export interface AirFreightOffsetState {
  mode?: FlightOffsetMode;
  amount?: number;
  totalKilosCo2?: number;
  totalKilosCo2Avoided?: number;
  totalKilosCo2Offset?: number;
  vat?: number;
  customerVat?: number;
  excise?: number;
  customerExcise?: number;
  customerPrice?: number;
  price?: number;
  currency?: string;
  from?: AirportOption;
  to?: AirportOption;
  via?: AirportOption;
  weight?: number;
  flightPax?: number;
  distance?: number;
  distanceFromVia?: number;
  distanceViaTo?: number;
  isLoading: boolean;
  selectedPortfolioId?: string;
  orderName?: string;
  orderNumber?: string;
  orderDate?: string;
  orderId?: string;
  country?: Option;
  department?: Option;
  description?: string;
  emissionScope?: Option;
  timeFrameFrom?: Date;
  timeFrameTo?: Date;
  isOrderInfoInvalid?: boolean;
  selectedCustomMetadata?: string[];
  byDistanceAirFreights: ByDistanceAirFreight[];
  fileUploading?: boolean;
  fileProcessing?: boolean;
  progressPercentage?: number;
  isUploadingFileCancelled?: boolean;
  attachment?: { storageBlobName: string; type: string };
  fileErrors?: string[];
  fileSuccess?: boolean;
  isPaidByPartner?: boolean;
  customer?: PartnersCustomerOption;
  claimCode?: string;
  inviteId?: string;
  isMultiUpload?: boolean;
  attachmentTempUrl?: string;
  customersCount?: number;
  billId?: string;
  isMultiplierActive?: boolean;
  orderStatus?: string;
  fullOffsetRows?: undefined;
  costCenter?: string;
  poNumber?: string;
  [key: string]:
    | string
    | Option
    | Date
    | number
    | undefined
    | boolean
    | string[]
    | ByDistanceAirFreight[]
    | PartnersCustomerOption
    | { storageBlobName: string; type: string };
}

interface OrderResponse {
  orderId: string;
  order: Order;
  stripePaymentIntentId?: string;
  stripeClientSecret?: string;
  stripePaymentMethodId?: string;
  claimCode?: string;
  inviteId?: string;
  fileUrl?: string;
  billId?: string;
}

//endregion

const initialState: AirFreightOffsetState = {
  isLoading: false,
  byDistanceAirFreights: [],
  isOrderInfoInvalid: true,
  isMultiUpload: true,
  mode: 'upload',
};

const tonnesFactor = 1000;

const slice = createSlice({
  name: 'offsetAirFreight',
  initialState,
  reducers: {
    reset: (): AirFreightOffsetState => {
      return initialState;
    },
    resetWithoutPortfolioId: (state: AirFreightOffsetState): AirFreightOffsetState => {
      return { ...initialState, selectedPortfolioId: state.selectedPortfolioId };
    },
    resetAmountAndPrice: (state: AirFreightOffsetState): AirFreightOffsetState => {
      return { ...state, amount: 0, totalKilosCo2: 0, price: 0 };
    },
    resetCustomer: (state: AirFreightOffsetState): AirFreightOffsetState => {
      return { ...state, customer: undefined };
    },
    setLoading: (state: AirFreightOffsetState, action: PayloadAction<boolean>): AirFreightOffsetState => {
      state.isLoading = action.payload;
      return state;
    },
    setOrderInfo: (state: AirFreightOffsetState, action: PayloadAction<OffsetInfo>): AirFreightOffsetState => {
      Object.keys(action.payload).map(key => {
        state[key] = action.payload[key];
      });
      return state;
    },
    setOrderInfoInvalid: (state: AirFreightOffsetState, action: PayloadAction<boolean>): AirFreightOffsetState => {
      state.isOrderInfoInvalid = action.payload;
      return state;
    },
    setOrder: (state: AirFreightOffsetState, action: PayloadAction<OrderPayload>): AirFreightOffsetState => {
      state.orderNumber = action.payload.number;
      state.orderDate = action.payload.date;
      state.orderName = action.payload.name ?? '';
      state.orderId = action.payload.id;
      state.inviteId = action.payload.inviteId;
      state.claimCode = action.payload.claimCode;
      state.billId = action.payload.billId;
      state.orderStatus = action.payload.orderStatus;
      return state;
    },
    setSelectedCustomMetadata: (state: AirFreightOffsetState, action: PayloadAction<string[]>): AirFreightOffsetState => {
      state.selectedCustomMetadata = action.payload;
      return state;
    },
    setOrderVat: (state: AirFreightOffsetState, action: PayloadAction<number>): AirFreightOffsetState => {
      state.vat = action.payload;
      return state;
    },
    setCustomerVat: (state: AirFreightOffsetState, action: PayloadAction<number>): AirFreightOffsetState => {
      state.customerVat = action.payload;
      return state;
    },
    resetUploadState: (state: AirFreightOffsetState, action: PayloadAction<boolean>): AirFreightOffsetState => {
      if (!action.payload) {
        source.cancel();
      }
      state.fileUploading = action.payload;
      state.fileProcessing = action.payload;
      state.progressPercentage = 0;
      state.isUploadingFileCancelled = true;
      state.fileErrors = [];
      state.attachmentTempUrl = undefined;
      state.attachment = undefined;
      state.fileSuccess = false;
      state.amount = undefined;
      state.totalKilosCo2 = undefined;
      state.customersCount = undefined;
      return state;
    },
    setFileUploading: (state: AirFreightOffsetState, action: PayloadAction<boolean>): AirFreightOffsetState => {
      state.fileUploading = action.payload;
      return state;
    },
    setAttachment: (state: AirFreightOffsetState, action: PayloadAction<{ storageBlobName: string; type: string }>): AirFreightOffsetState => {
      state.attachment = action.payload;
      return state;
    },
    setAttachmentTempUrl: (state: AirFreightOffsetState, action: PayloadAction<string>): AirFreightOffsetState => {
      state.attachmentTempUrl = action.payload;
      return state;
    },
    setCustomersCount: (state: AirFreightOffsetState, action: PayloadAction<number>): AirFreightOffsetState => {
      state.customersCount = action.payload;
      return state;
    },
    setFileErrors: (state: AirFreightOffsetState, action: PayloadAction<string[]>): AirFreightOffsetState => {
      state.fileErrors = action.payload;
      return state;
    },
    setFileSuccess: (state: AirFreightOffsetState, action: PayloadAction<boolean>): AirFreightOffsetState => {
      state.fileSuccess = action.payload;
      return state;
    },
    setUploadTotalAmount: (state: AirFreightOffsetState, action: PayloadAction<{ amount: number; kilosCo2: number }>): AirFreightOffsetState => {
      state.amount = fixFloat(action.payload.amount);
      state.totalKilosCo2 = action.payload.kilosCo2;
      return state;
    },
    setIsMultiplierActive: (state: AirFreightOffsetState, action: PayloadAction<boolean>): AirFreightOffsetState => {
      state.isMultiplierActive = action.payload;
      return state;
    },
    setOffsetPrice: (
      state: AirFreightOffsetState,
      action: PayloadAction<{ price: number; vat: number; customerPrice: number; customerVat: number; excise: number; customerExcise: number }>,
    ): AirFreightOffsetState => {
      state.price = action.payload.price;
      state.vat = action.payload.vat;
      state.excise = action.payload.excise;
      state.customerPrice = action.payload.customerPrice;
      state.customerVat = action.payload.customerVat;
      state.customerExcise = action.payload.customerExcise;
      return state;
    },
    setProgressPercentage: (state: AirFreightOffsetState, action: PayloadAction<number>): AirFreightOffsetState => {
      state.progressPercentage = action.payload;
      return state;
    },
    setFileProcessing: (state: AirFreightOffsetState, action: PayloadAction<boolean>): AirFreightOffsetState => {
      state.fileProcessing = action.payload;
      return state;
    },
    resetUploadingFileCancelled: (state: AirFreightOffsetState): AirFreightOffsetState => {
      state.isUploadingFileCancelled = false;
      return state;
    },
    setEmissionsAvoided: (state: AirFreightOffsetState, action: PayloadAction<number>): AirFreightOffsetState => {
      state.totalKilosCo2Avoided = action.payload;
      return state;
    },
    setEmissionsOffset: (state: AirFreightOffsetState, action: PayloadAction<number>): AirFreightOffsetState => {
      state.totalKilosCo2Offset = action.payload;
      return state;
    },
  },
});

const {
  reset,
  resetWithoutPortfolioId,
  setOrderInfo,
  setLoading,
  resetAmountAndPrice,
  setOrderInfoInvalid,
  setSelectedCustomMetadata,
  setOrder,
  setOrderVat,
  setCustomerVat,
  resetUploadState,
  setFileUploading,
  setFileErrors,
  setFileSuccess,
  setUploadTotalAmount,
  setAttachment,
  setAttachmentTempUrl,
  setCustomersCount,
  setIsMultiplierActive,
  resetCustomer,
  setOffsetPrice,
  setProgressPercentage,
  setFileProcessing,
  resetUploadingFileCancelled,
  setEmissionsAvoided,
  setEmissionsOffset,
} = slice.actions;

export {
  reset,
  resetWithoutPortfolioId,
  setOrderInfo,
  resetAmountAndPrice,
  setOrderInfoInvalid,
  setSelectedCustomMetadata,
  setOrderVat,
  setCustomerVat,
  resetUploadState,
  setIsMultiplierActive,
  resetCustomer,
};
export default slice.reducer;

const saveUsedAirports = (usedAirports: AirportOption[]): AppThunk => (): void => {
  try {
    let savedAirports = JSON.parse(localStorage.getItem('usedAirports') ?? '[]') as AirportOption[];
    savedAirports = savedAirports.filter(s => usedAirports.findIndex(u => u.value === s.value) === -1);
    savedAirports.unshift(...usedAirports);
    const maxSavedAirports = 5;
    savedAirports = savedAirports.slice(0, maxSavedAirports);
    localStorage.setItem('usedAirports', JSON.stringify(savedAirports));
  } catch {
    //err
  }
};

function generateFlightMetadata(): Metadata {
  return {
    order: {
      channel: 'portal-fulloffset',
    },
  };
}

export const getAirFreightOrderObject = ({
  offsetProps,
  currentUser,
  virtualPortfolios,
  mainPortfolioId,
  userCurrency,
  customer,
}: {
  offsetProps: AirFreightOffsetState;
  currentUser: Account | undefined;
  virtualPortfolios: {
    portfolioId: string;
    weight: number;
  }[];
  mainPortfolioId: string | undefined;
  isMultiUpload?: boolean;
  userCurrency: string;
  customer: PartnersCustomerOption | undefined;
}): OrderParams => {
  const { isMultiplierActive, amount } = offsetProps;
  const tonnesFactor = 1000;

  const totalKilosCo2 = Number(amount ?? 0) * tonnesFactor;
  const portfolioId = mainPortfolioId ?? null;

  const multiplierMode = currentUser?.partnership?.settings?.portal?.multiplier?.mode;
  const isDiscountMode = multiplierMode === 'Discount';
  const multiplierValue = currentUser?.partnership?.settings?.portal?.multiplier?.value ?? 0;
  const discountValue = multiplierValue / (1 + multiplierValue);
  const discountedOffset = isMultiplierActive && multiplierValue ? (totalKilosCo2 ?? 0) * discountValue : 0;
  const mappedVirtualPortfolios = virtualPortfolios.map(({ portfolioId, weight }) => ({
    id: portfolioId,
    weight: isMultiplierActive && isDiscountMode ? weight * discountValue : weight,
  }));

  const order = {
    currency: userCurrency,
    category: 'AirFreightSubscription',
    items: [
      {
        type: 'impact',
        impact: 'Co2',
        kilosCo2: isDiscountMode ? totalKilosCo2 - discountedOffset : totalKilosCo2,
        portfolio: mainPortfolioId
          ? undefined
          : {
              type: 'Virtual',
              impact: 'co2',
              currency: userCurrency,
              childPortfolios: mappedVirtualPortfolios,
            },
        portfolioId,
        feeFactors: {
          airFreight: true,
        },
        taxFactors: {
          customerCountry: customer?.country,
          customerCountrySubdivision: customer?.countrySubdivision,
        },
      },
    ],
  };

  return order;
};

export const getPriceForOrder = (): AppThunk => async (dispatch, getState): Promise<void> => {
  const airFreightOffset = getState().airFreight;
  const { customer, currency } = airFreightOffset;
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const virtualPortfolios = virtualPortfoliosSelector(getState());
  const mainPortfolioId = singlePorfolioSanityIdSelector(getState());
  const orderApi = createOrderApi(context);
  const userCurrency = currency ?? 'USD';
  const customerCurrency = customer?.currency ?? 'USD';
  const customerId = currentUser?.customerId ?? '';
  const partnershipId = currentUser?.partnershipId ?? '';
  const isConnectContext = context === 'connect';

  try {
    const order = getAirFreightOrderObject({
      offsetProps: airFreightOffset,
      currentUser,
      virtualPortfolios,
      mainPortfolioId,
      userCurrency,
      customer,
    });

    validateItemPortfolio(order.items?.[0]);

    const response =
      context === 'connect' ? await orderApi.getQuoteForPartner(order, partnershipId) : await orderApi.getQuoteForCustomer(order, customerId);

    let customerPrice = response.totalPrice;
    let customerVat = response.totalVat;
    let customerExcise = response.totalExcise;
    let emissionsAvoided = response.totalKilosCo2Avoided;
    let emissionsOffset = response.totalKilosCo2Offset;

    if (isConnectContext && customer && customerCurrency !== userCurrency) {
      const customerOrder = getAirFreightOrderObject({
        offsetProps: airFreightOffset,
        currentUser,
        virtualPortfolios,
        mainPortfolioId,
        userCurrency: customerCurrency,
        customer,
      });
      const customerResponse = await orderApi.getQuoteForPartner(customerOrder, partnershipId);
      customerPrice = customerResponse.totalPrice;
      customerVat = customerResponse.totalVat;
      customerExcise = customerResponse.totalExcise;
      emissionsAvoided = customerResponse.totalKilosCo2Avoided;
      emissionsOffset = customerResponse.totalKilosCo2Offset;
    }

    dispatch(
      setOffsetPrice({
        price: response.totalPrice,
        vat: response.totalVat,
        excise: response.totalExcise,
        customerPrice,
        customerVat,
        customerExcise,
      }),
    );

    dispatch(setEmissionsAvoided(emissionsAvoided ?? 0));
    dispatch(setEmissionsOffset(emissionsOffset ?? 0));
  } catch (err) {
    //@ts-ignore
    if (err.data?.includes('Insufficient supply')) {
      dispatch(
        showToast({
          variant: 'error',
          titlei18nKey: 'offset:corporate.portfolios.errorTitle',
          descriptioni18nKey: 'offset:corporate.portfolios.insufficientSupplyErrorMessage',
        }),
      );
      //@ts-ignore
    } else if (err.data) {
      dispatch(showToast({ variant: 'error', titlei18nKey: 'offset:errorTitle', descriptioni18nKey: 'offset:fetchPriceError' }));
    }
  }
};

export const createAirFreightOrder = (errorCb?: CallableFunction, successCb?: CallableFunction): AppThunk => async (
  dispatch,
  getState,
): Promise<void> => {
  dispatch(setLoading(true));
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const { isPaidByPartner, customer, currency, attachment, isMultiplierActive, ...restProps } = getState().airFreight;
  const virtualPortfolios = virtualPortfoliosSelector(getState());
  const mainPortfolioId = singlePorfolioSanityIdSelector(getState());
  const metadata = metadatasSelector(getState()) ?? [];
  const portfolioId = mainPortfolioId ?? null;
  const orderCurrency = !isPaidByPartner && customer?.currency ? customer.currency ?? 'USD' : currency ?? 'USD';

  const orderApi = createOrderApi(context);
  const { airFreight, user } = getState();

  const multiplierValue = currentUser?.partnership?.settings?.portal?.multiplier?.value ?? 0;

  const customMetadata: {
    internalName: string;
    displayName: string;
    value: string | Date;
  }[] = [];

  const customMetadatas = {
    ...omit(restProps, [
      'price',
      'customerPrice',
      'isLoading',
      'amount',
      'currency',
      'isOrderInfoInvalid',
      'vat',
      'customerVat',
      'excise',
      'customerExcise',
      'orderName',
      'flightPax',
      'distanceFromVia',
      'distanceViaTo',
      'from',
      'to',
      'via',
      'flights',
      'description',
      'kilosCo2',
      'totalKilosCo2',
      'fileUploading',
      'fileProcessing',
      'progressPercentage',
      'fileErrors',
      'fileSuccess',
      'selectedCustomMetadata',
      'isMultiUpload',
      'attachmentTempUrl',
      'customersCount',
      'inviteId',
      'claimCode',
      'totalKilosCo2Avoided',
      'totalKilosCo2Offset',
    ]),
  };

  Object.keys(customMetadatas).map(key => {
    let value: string | Date = '';

    if ((airFreight[key] as Option)?.value) {
      value = (airFreight[key] as Option)?.value;
    } else {
      value = airFreight[key] as string | Date;
    }
    if (value) {
      customMetadata.push({
        internalName: key,
        value: value,
        displayName: getDisplayName(metadata, key),
      });
    }
  });

  try {
    const metadata = generateFlightMetadata();

    const order = {
      description: airFreight.orderName ?? '',
      comment: airFreight.description ?? '',
      currency: orderCurrency,
      category: 'AirFreightSubscription',
      items: [
        {
          type: 'impact',
          impact: 'Co2',
          ...(mainPortfolioId ? {} : { virtualPortfolios }),
          portfolioId,
        },
      ],
      metadata: {
        ...metadata,
        custom: customMetadata,
      },
      multiplier: isMultiplierActive ? { value: multiplierValue } : undefined,
    };

    let orderResult: OrderResponse;
    if (context === 'connect') {
      dispatch(setFileProcessing(true));
      const orderBody = {
        ...order,
        facilitated: {
          isFacilitated: true,
          isPaidByPartner: isPaidByPartner ?? true,
        },
        attachments: attachment ? [attachment] : [],
      };

      const batchResponse = await orderApi.createBatchOrderJobForPartner(orderBody, user.currentAccount?.partnershipId ?? '');
      const result = await checkJob<PlaceOrderSummary>({
        partnershipId: batchResponse.partnershipId,
        jobId: batchResponse.jobId,
        context,
        module: 'airFreight',
        setFileProcessingCb: (state: boolean) => dispatch(setFileProcessing(state)),
        setFileUploadingCb: (state: boolean) => dispatch(setFileUploading(state)),
        setProgressPercentageCb: (percentage: number) => dispatch(setProgressPercentage(percentage)),
      });
      orderResult = { ...result.summary.facilitatedBatchOrderResult.order, fileUrl: result.summary.facilitatedBatchOrderResult.fileUrl };
    } else {
      orderResult = await orderApi.create(order, user.currentAccount?.customerId ?? '');
    }

    if (orderResult.order) {
      if (context !== 'connect' || isPaidByPartner) {
        setTag({
          ...tags.portalOffset.placeOrder,
          categories: [orderResult.order.category],
          transactionId: orderResult.orderId,
          transactionTotal: orderResult.order.totalPrice,
          currencyCode: orderResult.order.currency,
          transactionProducts: orderResult.order.items.map(item => ({
            category: orderResult.order.category,
            name: `${transactionProductsName.flight} offset`,
            price: item.price,
            quantity: item.kilosCo2,
            sku: `${transactionProductsName.flight.toLowerCase()}-offset`,
          })),
        });
      }

      dispatch(
        setOrder({
          number: orderResult.order.choooseId,
          date: orderResult.order.createdDate,
          name: airFreight.orderName ?? '',
          id: orderResult.orderId,
          claimCode: orderResult.claimCode,
          inviteId: orderResult.inviteId,
          billId: orderResult.billId,
          orderStatus: getOrderStatus(orderResult.order),
        }),
      );
    }

    dispatch(setAttachmentTempUrl(orderResult.fileUrl ?? ''));
    dispatch(syncAttribution());
    dispatch(loadBills());

    if (airFreight.from && airFreight.to) {
      if (airFreight.via) {
        dispatch(saveUsedAirports([airFreight.from, airFreight.via, airFreight.to]));
      } else {
        dispatch(saveUsedAirports([airFreight.from, airFreight.to]));
      }
    }
    dispatch(setLoading(false));
    successCb && successCb();
  } catch (err) {
    dispatch(setLoading(false));
    dispatch(showToast({ variant: 'error', titlei18nKey: 'offset:errorTitle', descriptioni18nKey: 'offset:createOrderError' }));
    errorCb && errorCb();
  }
};

export const processFile = (file: File): AppThunk => async (dispatch, getState): Promise<void> => {
  dispatch(setFileSuccess(false));
  dispatch(setFileProcessing(true));
  dispatch(setUploadTotalAmount({ amount: 0, kilosCo2: 0 }));
  dispatch(resetUploadingFileCancelled());
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const airFreightAPI = createAirFreightApi(context);
  const currency = getState().airFreight.currency ?? 'USD';

  try {
    source = CancelToken.source();

    const batchResponse = await airFreightAPI.createMultiCustomerBatchOrderForPartner(
      currentUser?.partnershipId ?? 'default',
      currency,
      file,
      source.token,
    );
    if (batchResponse.partnershipId && batchResponse.jobId) {
      const response = await checkJob<FlightsProcessingFileSummary>({
        partnershipId: batchResponse.partnershipId,
        jobId: batchResponse.jobId,
        context,
        module: 'airFreight',
        setFileProcessingCb: (state: boolean) => dispatch(setFileProcessing(state)),
        setFileUploadingCb: (state: boolean) => dispatch(setFileUploading(state)),
        setProgressPercentageCb: (percentage: number) => dispatch(setProgressPercentage(percentage)),
      });
      const processedFile = response?.summary?.processedFile;
      const customersCount = processedFile?.details?.multiCustomerFlights?.customersCount;
      const errorMessages = response?.messages?.filter(({ type }) => type.toLowerCase() !== 'information')?.map(m => m.message);

      if (processedFile) {
        customersCount && dispatch(setCustomersCount(customersCount));
        dispatch(setAttachment(processedFile.attachment));
        dispatch(setAttachmentTempUrl(processedFile.attachmentTempUrl));
        const kilosCo2 = processedFile.priceQuote.items[0].kilosCo2;
        dispatch(setUploadTotalAmount({ amount: kilosCo2 / tonnesFactor, kilosCo2 }));
        dispatch(setFileErrors([]));
        dispatch(setFileSuccess(true));
      } else if (Array.isArray(errorMessages) && errorMessages.length > 0) {
        dispatch(setFileErrors(errorMessages));
      } else {
        dispatch(setFileErrors([]));
      }
    }
  } catch (err) {
    // @ts-ignore
    if (err && err.data) {
      // @ts-ignore
      const { data } = err.data;
      dispatch(setFileErrors(data ? [...data] : []));
    }
    dispatch(setFileUploading(false));
  }
};

export const airFreightAmountSelector = createSelector(
  (state: RootState) => state.airFreight,
  state => {
    const { amount } = state;

    return fixFloat(amount ?? 0);
  },
);
